import {
  EMPTY_DATE,
  DATE_LENGTH,
  RANGE_DELIMETER,
  RU_FORMAT_PATTERN,
  ISO_FORMAT_PATTERN,
} from "./constants";
import { DateValue, SingleDateValue, DateMatchGroups } from "./types";

export const isRangeMode = <T>(value: T | T[]): value is T[] =>
  Array.isArray(value);

export const isFilledDate = (
  value: DateValue,
  options: {
    pattern?: RegExp;
    isPartial?: boolean;
  } = {}
) => {
  const { pattern = ISO_FORMAT_PATTERN, isPartial = false } = options;

  if (isRangeMode(value)) {
    return isPartial
      ? value.some((date) => date && pattern.test(date))
      : value.every((date) => date && pattern.test(date));
  }

  return pattern.test(value);
};

/**
 * Функция проверяет полностью ли заполнены в формате RU строка или массив строк в соответствии с флагом isPartial
 *
 * Пример: isFilledDateRu(["17.06.2022",""],true) -> true
 *         isFilledDateRu(["17.06.2022",""]) -> false
 */
export const isFilledDateRu = (value: DateValue, isPartial = false) =>
  isFilledDate(value, { pattern: RU_FORMAT_PATTERN, isPartial });

export const isEmptyDate = (value: DateValue) => {
  if (isRangeMode(value)) {
    return !value.join("");
  }

  return !value;
};

export const formatDateToISO = (value: DateValue) => {
  const formatToISO = (date: SingleDateValue) => {
    try {
      const {
        groups: { day, month, year },
      } = RU_FORMAT_PATTERN.exec(date) as DateMatchGroups;

      return `${year}-${month}-${day}`;
    } catch {
      return "";
    }
  };

  return isRangeMode(value)
    ? value.map((date) => date && formatToISO(date))
    : value && formatToISO(value);
};

export const formatDateToLocale = (value: DateValue) => {
  const formatToLocale = (date: SingleDateValue) => {
    try {
      const {
        groups: { year, month, day },
      } = ISO_FORMAT_PATTERN.exec(date) as DateMatchGroups;

      return `${day}.${month}.${year}`;
    } catch {
      return "";
    }
  };

  return isRangeMode(value)
    ? value.map((date) => date && formatToLocale(date))
    : value && formatToLocale(value);
};

export const getInputValue = (value: DateValue) => {
  if (isRangeMode(value)) {
    const delimeter = isEmptyDate(value) ? EMPTY_DATE : RANGE_DELIMETER;

    return value.join(delimeter);
  }

  return value;
};

export const maskDate = (value: string) => {
  const date = value.replace(/\D/g, "").slice(0, DATE_LENGTH);

  if (date.length >= 5) {
    return `${date.slice(0, 2)}.${date.slice(2, 4)}.${date.slice(4)}`;
  }

  if (date.length >= 3) {
    return `${date.slice(0, 2)}.${date.slice(2)}`;
  }

  return date;
};

/**
 * Функция для получения значения инпута в зависимости от типа календаря
 *
 * Пример: getInputValueByMode("17.06.2022 — 20.12.2022", true) -> ["17.06.2022", "20.12.2022"]
 *         getInputValueByMode("17.06.2022", false) -> "17.06.2022"
 */
export const getInputValueByMode = (value: string, isRange: boolean) =>
  isRange ? value.split(RANGE_DELIMETER) : value;

/**
 * Функция для получения пустого значения даты в зависимости от типа календаря
 *
 * Пример: getEmptyDate(true) -> ["", ""]
 *         getEmptyDate(false) -> ""
 */
export const getEmptyDate = (isRange: boolean) =>
  isRange ? [EMPTY_DATE, EMPTY_DATE] : EMPTY_DATE;

/**
 * Функция для конвертации строки в формате RU или ISO в массив цифр.
 *
 * Пример: dateStringToDate("17.06.2022", ".") -> [17, 6, 2022]
 *         dateStringToDate("2022-06-17", "-") -> [2022, 6, 17]
 */
const getDateNumbersFromStringDate = (
  dateString: string,
  delimeter: "." | "-"
) =>
  dateString
    .split(delimeter)
    .splice(0, 3)
    .map((element) => Number(element));

/**
 * Функция для конвертации строки в формате RU или ISO в дату.
 *
 * Пример: dateStringToDate("") -> null
 *         dateStringToDate("17.06.2022") -> Fri Jun 17 2022 00:00:00 GMT+0300 (Москва, стандартное время)
 *         dateStringToDate("2022-06-17") -> Fri Jun 17 2022 00:00:00 GMT+0300 (Москва, стандартное время)
 */
export const dateStringToDate = (value: string) => {
  if (isFilledDateRu(value)) {
    const [dd, mm, yyyy] = getDateNumbersFromStringDate(value, ".");

    return new Date(yyyy, mm - 1, dd);
  }

  if (isFilledDate(value)) {
    const [yyyy, mm, dd] = getDateNumbersFromStringDate(value, "-");

    return new Date(yyyy, mm - 1, dd);
  }

  return null;
};

/**
 * Функция для перевода строки даты в формате RU или ISO в миллисекунды
 *
 * Пример: dateStringToMs("") -> NaN
 *         dateStringToMs("17.06.2022") -> 1655413200000
 */
export const dateStringToMs = (dateString: string) =>
  dateStringToDate(dateString)?.valueOf() || NaN;

/**
 * Функция для получения корректного значения даты в случает несоответствия заданному диапазону
 *
 * Пример: getCorrectDate("2022-06-17","2022-06-20","17.05.2022") -> "17.06.2022"
 *         getCorrectDate("2022-06-17","2022-06-20","11.05.2022") -> "17.06.2022"
 */
export const getCorrectDate = (
  minDate: string,
  maxDate: string,
  value: DateValue
): DateValue => {
  if (!isFilledDateRu(value, true)) {
    return value;
  }

  // TODO: провести рефакторинг методов форматирования строки и устранить полиморфность типов
  const minDateRu = formatDateToLocale(minDate) as string;
  const maxDateRu = formatDateToLocale(maxDate) as string;

  const minDateMs = dateStringToMs(minDate);
  const maxDateMs = dateStringToMs(maxDate);

  if (isRangeMode(value)) {
    let [startDate, endDate] = value;

    const startDateMs = dateStringToMs(startDate);
    const endDateMs = dateStringToMs(endDate);

    if (minDateMs > startDateMs) {
      startDate = minDateRu;
    }

    if (endDateMs > maxDateMs) {
      endDate = maxDateRu;
    }

    if (dateStringToMs(startDate) > dateStringToMs(endDate)) {
      return getCorrectDate(minDate, maxDate, [endDate, startDate]);
    }

    return [startDate, endDate];
  }

  const dateMs = dateStringToMs(value);

  if (minDateMs > dateMs) {
    return minDateRu;
  }

  if (dateMs > maxDateMs) {
    return maxDateRu;
  }

  return value;
};

/**
 * Функция проводящая сравнение нового значения даты с предыдущим
 *
 * Пример: isEqualDate(["17.06.2022", "21.06.2022"], ["17.06.2022", "21.06.2022"]) -> true
 *         isEqualDate(["17.06.2022", ""], ["", ""]) -> false
 */
export const isEqualDate = (
  isoDateString: DateValue,
  previousIsoDateString: DateValue
) => {
  if (isRangeMode(isoDateString) && isRangeMode(previousIsoDateString)) {
    return isoDateString.join("") === previousIsoDateString.join("");
  }

  return isoDateString === previousIsoDateString;
};
